import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Screen from "../home/content/Screen";
import { SiDatabricks } from "react-icons/si";
import { Auth } from "../../context/admin/Authentication";
import { DashboardIntro, MyButton } from "../home/component/Component";
import { BreadCrumbComponent } from "../../middlewares/breadCrumb/BreadCrumbComponent";
import { getOneLevel, getOneCountry } from "../../middlewares/breadCrumb/BreadCrumb";
import { useAdminContext } from "../../context/admin/context/AdminContext";
import { _TYPES_ } from "./utils/types";
import { getType } from "../home/content/contentStats/helper";
import { RiSearchLine, RiCloseLine } from "react-icons/ri";

export const DataContent = () => {
  const navigate = useNavigate();
  const { sysAdmin, setSingleAdmin } = useAdminContext();
  const role = sysAdmin?.role;
  const location =
    getOneLevel()?.length > 0 ? getOneLevel()[0]?.name : getOneCountry()[0]?.name;

  const allItems = useMemo(
    () => getData(location, ["super_admin", "admin"].includes(role))?.filter(Boolean) || [],
    [location, role]
  );

  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return allItems;
    return allItems.filter((it) => (it.name || "").toLowerCase().includes(term));
  }, [q, allItems]);

  // Quick-open: Enter to open first visible result
  const quickOpen = () => {
    if (!filtered.length) return;
    const top = filtered[0];
    // keep your existing context behavior
    setSingleAdmin({
      ids: [sysAdmin?.admin_id],
      type: getType({ field: top.key }),
      route: "/dosee/data",
    });
    navigate(top.link);
  };

  // handy suggestions to hint what to search
  const suggestions = useMemo(
    () =>
      allItems
        .slice(0, 6)
        .map((x) => x.name)
        .sort((a, b) => a.localeCompare(b)),
    [allItems]
  );

  return (
    <Auth
      children={
        <Screen
          title={`${location}`}
          component={
            <div className="p-3 lg:p-5 h-full overflow-y-auto rounded-b-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 w-full">
              <BreadCrumbComponent />
              <DashboardIntro props={{ intro: `Data Content`, name: location }} />

              {/* Search toolbar */}
              <div className="w-full max-w-6xl mx-auto mb-4">
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-3 sm:p-4">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          value={q}
                          onChange={(e) => setQ(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              quickOpen();
                            }
                          }}
                          placeholder="Search sections… e.g., Events, Hotel, Museum"
                          className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                          aria-label="Search data sections"
                        />
                        {q && (
                          <button
                            type="button"
                            onClick={() => setQ("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full hover:bg-gray-100 text-gray-500 flex items-center justify-center"
                            aria-label="Clear search"
                          >
                            <RiCloseLine />
                          </button>
                        )}
                      </div>

                      {/* Results count */}
                      <span className="hidden sm:inline text-xs text-gray-500 whitespace-nowrap">
                        Showing {filtered.length} of {allItems.length}
                      </span>
                    </div>

                    {/* Hint / suggestions */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] text-gray-500">Try:</span>
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setQ(s)}
                          className="text-[11px] px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                          aria-label={`Search for ${s}`}
                        >
                          {s}
                        </button>
                      ))}
                      <span className="ml-auto text-[11px] text-gray-400">
                        Tip: Press <kbd className="px-1 rounded border border-gray-300 bg-gray-50">Enter</kbd> to open the top result
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4 selection:text-white selection:bg-green-900 pt-1 max-w-6xl mx-auto">
                {filtered.length ? (
                  filtered.map((value, index) => (
                    <Content
                      key={`${value.link}-${index}`}
                      index={index}
                      name={value?.name}
                      link={value?.link}
                      type={value?.key}
                      location={location}
                    />
                  ))
                ) : (
                  <div className="col-span-2 md:col-span-4 xl:col-span-6">
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center">
                      <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <RiSearchLine className="text-gray-400 text-xl" />
                      </div>
                      <h3 className="mt-3 text-sm font-semibold text-gray-900">
                        No matching sections
                      </h3>
                      <p className="mt-1 text-xs text-gray-500">
                        Try a different term or clear the search.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          }
        />
      }
    />
  );
};

const Content = ({ name, link, type, index, location }) => {
  const { sysAdmin, setSingleAdmin } = useAdminContext();

  const palettes = [
    { grad: "from-emerald-500 via-teal-500 to-cyan-500", soft: "bg-emerald-50", icon: "text-emerald-600" },
    { grad: "from-fuchsia-500 via-pink-500 to-rose-500", soft: "bg-pink-50", icon: "text-fuchsia-600" },
    { grad: "from-indigo-500 via-violet-500 to-purple-500", soft: "bg-violet-50", icon: "text-indigo-600" },
    { grad: "from-amber-500 via-orange-500 to-red-500", soft: "bg-amber-50", icon: "text-amber-600" },
    { grad: "from-sky-500 via-blue-500 to-indigo-500", soft: "bg-sky-50", icon: "text-sky-600" },
    { grad: "from-lime-500 via-green-500 to-emerald-500", soft: "bg-lime-50", icon: "text-lime-700" },
  ];
  const tone = palettes[index % palettes.length];

  const blurb = `Create, edit & publish ${name.toLowerCase()} for ${location}.`;
  const features = "Organize • Review • Publish";

  return (
    <div className="group relative h-full">
      <div className={`rounded-2xl p-[1px] bg-gradient-to-br ${tone.grad} shadow-sm transition-transform duration-200 will-change-transform group-hover:translate-y-[-2px]`}>
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 p-4 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className={`shrink-0 h-10 w-10 rounded-xl ${tone.soft} ring-1 ring-black/5 flex items-center justify-center`}>
              <SiDatabricks className={`text-lg ${tone.icon}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{name}</h3>
              <p className="mt-0.5 text-[11px] text-gray-500 line-clamp-2">{blurb}</p>
            </div>
          </div>

          {/* Feature chip */}
          <div className="mt-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] text-gray-700">
              <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-r ${tone.grad}`} />
              {features}
            </div>
          </div>

          {/* Footer with CTA pinned right */}
          <div className="mt-auto pt-4 flex items-center justify-between">
            <span className="text-[11px] text-gray-500">Section</span>
            <Link to={link} className="block" aria-label={`Open ${name}`}>
              <MyButton
                title="Open"
                className="px-4 py-2 rounded-xl bg-gray-900 hover:bg-gray-800 text-white shadow-sm"
                onClick={() => {
                  setSingleAdmin({
                    ids: [sysAdmin?.admin_id],
                    type: getType({ field: type }),
                    route: "/dosee/data",
                  });
                }}
              />
            </Link>
          </div>

          {/* Accent */}
          <div className={`mt-3 h-1 rounded-full bg-gradient-to-r ${tone.grad} opacity-70 group-hover:opacity-100 transition`} />
        </div>
      </div>
    </div>
  );
};

const getData = (location, isSuperAdmin) => [
  { name: "Events", link: "/data/event", key: _TYPES_.EVENT },
  { name: "Attraction", link: "/data/attraction", key: _TYPES_.ATTRACTION },
  { name: "Cinema", link: "/data/cinema", key: _TYPES_.CINEMA },
  { name: "Music", link: "/data/music", key: _TYPES_.MUSIC },
  { name: "News", link: "/data/news", key: _TYPES_.NEWS },
  { name: "safaris", link: "/data/safari", key: _TYPES_.SAFARI },
  { name: "Restaurant", link: "/data/restaurant", key: _TYPES_.RESTAURANT },
  { name: "Things To Do", link: "/data/things/todo", key: _TYPES_.THINGSTODO },
  { name: "Party", link: "/data/party", key: _TYPES_.PARTY },
  { name: "Nightlife", link: "/data/nightlife", key: _TYPES_.NIGHTLIFE },
  { name: "City Guide", link: "/data/city/guide", key: _TYPES_.CITYGUIDE },
  { name: "Hotel", link: "/data/hotel", key: _TYPES_.HOTEL },
  { name: "Classes", link: "/data/class", key: _TYPES_.CLASSES },
  { name: "Shopping", link: "/data/shopping", key: _TYPES_.SHOPPING },
  { name: "Food", link: "/data/food", key: _TYPES_.FOOD },
  { name: "Book", link: "/data/book", key: _TYPES_.BOOK },
  { name: "Neighborhood", link: "/data/neighborhood", key: _TYPES_.NEIGHBORHOOD },
  { name: "Ministries", link: "/data/ministry", key: _TYPES_.MINISTRY },
  { name: "Getting Around", link: "/data/get/around", key: _TYPES_.GETTINGAROUND },
  { name: "Art", link: "/data/art", key: _TYPES_.ART },
  { name: "Movies", link: "/data/movie", key: _TYPES_.MOVIE },
  { name: "Dance", link: "/data/dance", key: _TYPES_.DANCE },
  { name: "Theater", link: "/data/theater", key: _TYPES_.THEATER },
  { name: "Museum", link: "/data/museum", key: _TYPES_.MUSEUM },
  { name: "Tips And Advice", link: "/data/tips/advice", key: _TYPES_.TIPSANDADVICE },
  isSuperAdmin ? { name: `Advertisement`, link: "/data/advertisement", key: _TYPES_.ADV } : null,
  { name: `Tour Package`, link: "/data/tour/package", key: _TYPES_.TOURPACKAGE },
  { name: `Product`, link: "/data/product", key: _TYPES_.PRODUCT },
  { name: `Companies`, link: "/data/company", key: _TYPES_.COMPANY },
  { name: `embassy`, link: "/data/embassy", key: _TYPES_.EMBASSY },
];
